import { Icon } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useField, useForm, useFormState } from 'react-final-form';
import { Level3Section } from '../Level3Section';
import { Wrapper, HeaderRow, Title, StyledDialog, QuestionsWrapper, ContentWrapper } from './components/ui';
import { Section, SectionNotes } from 'services/api/vehicle-sections/types';
import { ActivateButtonUI, useActivate } from '../components/activateButton';
import { useIsOnline } from 'hooks';
import { closeFinalReviewDialog, useFinalReviewDialogOpened } from 'services/listerUploadVehicle/v2';
import { useDispatch } from 'react-redux';
import toast, { ToastBar, Toaster } from 'react-hot-toast';
import { useMemo } from 'react';
import { FinalReviewNotesField } from './FinalReviewNotesField';
import { typeToLabel } from '../components/photos/constants';
import { FinalReviewObdImages } from './FinalReviewObdImages';

export const FinalReviewDialog = () => {
  const dispatch = useDispatch();

  const finalReviewOpen = useFinalReviewDialogOpened();
  const close = () => dispatch(closeFinalReviewDialog());

  return (
    <StyledDialog
      onBackdropClick={close}
      open={!!finalReviewOpen}
      isFullScreenMode={false}
      classes={{ paperScrollPaper: 'vehicle-questions' }}
    >
      {!!finalReviewOpen && <VehicleQuestionsDialogContent />}
    </StyledDialog>
  );
};

type NotesPathMetadata = {
  path: string;
  title: string;
};

function getSectionsWithNotes(
  values: Record<string, Section> & { inspector_notes?: SectionNotes },
  parentKey = '',
  parentLabel = ''
): NotesPathMetadata[] {
  const result: NotesPathMetadata[] = [];

  const { inspector_notes, ...sections } = values;

  if (inspector_notes && inspector_notes.message) {
    result.push({
      path: 'inspector_notes',
      title: 'Inspector Comments'
    });
  }

  for (const [key, section] of Object.entries(sections)) {
    const base = parentKey ? `${parentKey}.${key}` : key;
    const sectionLabel = section.label || parentLabel;

    // Section-level notes
    if (section.notes?.message?.trim()) {
      result.push({
        path: base,
        title: sectionLabel
      });
    }

    // Image notes
    section.images?.forEach((img, i) => {
      if (img.notes?.message?.trim()) {
        result.push({
          path: `${base}.images[${i}]`,
          title: img.type ? typeToLabel[img.type] : sectionLabel
        });
      }
    });

    // Checkbox notes
    section.checkboxes?.forEach((cb, i) => {
      if (cb.notes?.message?.trim()) {
        result.push({
          path: `${base}.checkboxes[${i}]`,
          title: section.label + '/' + cb.label
        });
      }
    });

    // Recursive subsections
    if (section.subsections && Object.keys(section.subsections).length > 0) {
      const subsectionResults = getSectionsWithNotes(section.subsections, `${base}.subsections`, sectionLabel);
      result.push(...subsectionResults);
    }
  }

  return result;
}

const VehicleQuestionsDialogContent = () => {
  const { handleList, shouldRender, listedAuctionActive } = useActivate();

  const dispatch = useDispatch();
  const close = () => dispatch(closeFinalReviewDialog());

  const isOnline = useIsOnline();

  const form = useFormState();

  const fieldsWithNotes = useMemo(() => getSectionsWithNotes(form.values), [form.values]);

  const {
    input: { value: inspectorNotes }
  } = useField('inspector_notes.message', { subscription: { value: true } });

  const handleSubmit = async () => {
    const obdImages = form.values?.interior_questions.subsections?.odb_check_smog_readiness?.images || [];

    if (obdImages.some(img => !img.url)) {
      toast.error('Please fill codes & smog readiness info to continue');
      return;
    }

    await handleList();
    close();
  };

  return (
    <Wrapper>
      <HeaderRow>
        <Title>Final Review</Title>
        <Icon onClick={close}>
          <Close />
        </Icon>
      </HeaderRow>
      <ContentWrapper>
        <QuestionsWrapper withPadding>
          {fieldsWithNotes.map(field => {
            if (field.path === 'inspector_notes') {
              return (
                <FinalReviewNotesField key={field.path} field={field.path} title={field.title} type="inspector_notes" />
              );
            }

            if (field.path.includes('.checkboxes')) {
              return <FinalReviewNotesField key={field.path} field={field.path} title={field.title} type="checkbox" />;
            }
            if (field.path.includes('.images')) {
              return <FinalReviewNotesField key={field.path} field={field.path} title={field.title} type="image" />;
            }
            return <Level3Section aiNotes notesOpened theme="white" key={field.path} sectionId={field.path} />;
          })}
          <FinalReviewObdImages />
        </QuestionsWrapper>

        {shouldRender ? (
          <ActivateButtonUI
            withMargin
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            type="button"
            disabled={!listedAuctionActive || !isOnline}
          >
            Submit Vehicle
          </ActivateButtonUI>
        ) : (
          <div style={{ height: '44px' }} />
        )}
      </ContentWrapper>

      <Toaster toasterId="final-review-toaster">
        {t => (
          <ToastBar
            toast={t}
            style={{
              ...t.style
            }}
          />
        )}
      </Toaster>
    </Wrapper>
  );
};
